import React, { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import Modal from '../common/Modal';

// Draft versions of one plan: list (who / when / variant / changed levels /
// quality dots), compare two versions (or a version with the current plan) and
// restore one. Server: /shifts/roster-periods/:id/versions…

const DOT = { green: 'var(--success)', amber: 'var(--warning)', red: 'var(--danger)', none: 'var(--border)' };
const KIND = { generated: 'Generated', before_restore: 'Before restore' };

const when = (s) => {
  try { return format(parseISO(`${s.replace(' ', 'T')}Z`), 'EEE d MMM, HH:mm'); } catch { return s; }
};

export function QualityDots({ quality = [] }) {
  return (
    <span style={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }} aria-label="Draft quality">
      {quality.map((q) => (
        <span
          key={q.key}
          title={`${q.label}: ${q.display}`}
          style={{ width: '9px', height: '9px', borderRadius: '50%', background: DOT[q.colour] || DOT.none, display: 'inline-block' }}
        />
      ))}
    </span>
  );
}

const levelText = (levels, keys, labels) => keys.map((k) => `${labels[k] || k}: ${levels[k]?.replace('_', ' ')}`).join(', ');

function VersionRow({ v, number, current, published, selected, onSelect, onRestore, canRestore, ruleLabels }) {
  return (
    <div
      style={{
        border: `1px solid ${current ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 'var(--radius)', padding: '10px 12px',
        background: current ? 'var(--primary-xlight)' : 'var(--white)', display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap',
      }}
    >
      <input
        type="checkbox"
        aria-label={`Select version ${v.id} for comparison`}
        checked={selected}
        onChange={() => onSelect(v.id)}
        style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: 'var(--primary-dark)' }}
      />
      <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: '0.84rem', lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700 }}>
          #{number} · {KIND[v.kind] || v.kind} · {when(v.created_at)}
          {current && <span style={{ marginLeft: '6px', color: 'var(--primary-dark)', fontSize: '0.74rem' }}>CURRENT</span>}
          {published && <span style={{ marginLeft: '6px', color: 'var(--success)', fontSize: '0.74rem' }}>PUBLISHED</span>}
        </div>
        <div style={{ color: 'var(--text-light)' }}>
          {v.created_by || 'System'} · variant {v.variant ?? '—'} · {v.filled}/{v.total} filled{v.pinned ? ` · 📌 ${v.pinned}` : ''}
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
          {v.changed_levels.length ? `Changed: ${levelText(v.levels, v.changed_levels, ruleLabels)}` : 'Default levels'}
        </div>
        <div style={{ marginTop: '4px' }}><QualityDots quality={v.quality} /></div>
      </div>
      {canRestore && !current && (
        <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '36px' }} onClick={() => onRestore(v)}>
          Restore
        </button>
      )}
    </div>
  );
}

function Compare({ data, labelOf, onHighlight }) {
  const diffColour = (row) => (row.a !== row.b ? 'var(--warning-light)' : 'transparent');
  return (
    <div style={{ marginTop: '14px' }}>
      <p style={{ margin: '0 0 8px', fontWeight: 700 }}>
        {labelOf(data.a)} ↔ {labelOf(data.b)}: {data.differing_count} shift{data.differing_count === 1 ? '' : 's'} differ
      </p>
      {data.differing_count > 0 && (
        <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '36px', marginBottom: '10px' }}
          onClick={() => onHighlight(data.differing_slot_ids)}>
          Show the differing shifts in the roster
        </button>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
              <th style={{ padding: '4px 6px' }}>Quality</th>
              <th style={{ padding: '4px 6px' }}>{labelOf(data.a)}</th>
              <th style={{ padding: '4px 6px' }}>{labelOf(data.b)}</th>
            </tr>
          </thead>
          <tbody>
            {data.quality.map((q) => (
              <tr key={q.key} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '4px 6px' }}>{q.label}</td>
                {[q.a, q.b].map((x, i) => (
                  <td key={i} style={{ padding: '4px 6px' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', marginRight: '5px', background: DOT[x?.colour] || DOT.none }} />
                    {x?.display ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginTop: '12px' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
              <th style={{ padding: '4px 6px' }}>Person</th>
              <th style={{ padding: '4px 6px' }}>Month</th>
              <th style={{ padding: '4px 6px' }}>Target</th>
              <th style={{ padding: '4px 6px' }}>{labelOf(data.a)}</th>
              <th style={{ padding: '4px 6px' }}>{labelOf(data.b)}</th>
            </tr>
          </thead>
          <tbody>
            {data.people.map((p) => (
              <tr key={`${p.user_id}-${p.month}`} style={{ borderTop: '1px solid var(--border)', background: diffColour(p) }}>
                <td style={{ padding: '4px 6px' }}>{p.name}</td>
                <td style={{ padding: '4px 6px' }}>{p.month}</td>
                <td style={{ padding: '4px 6px' }}>{p.target}</td>
                <td style={{ padding: '4px 6px', fontWeight: p.a !== p.b ? 700 : 400 }}>{p.a ?? '—'}</td>
                <td style={{ padding: '4px 6px', fontWeight: p.a !== p.b ? 700 : 400 }}>{p.b ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DraftVersionsModal({ period, onClose, onRestored, onHighlight }) {
  const [data, setData] = useState(null);
  const [ruleLabels, setRuleLabels] = useState({});
  useEffect(() => {
    api.get('/shifts/generator-settings')
      .then((r) => setRuleLabels(Object.fromEntries((r.data.rules || []).map((x) => [x.key, x.label]))))
      .catch(() => {});
  }, []);
  const [selected, setSelected] = useState([]);
  const [compare, setCompare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const isDraft = period?.status === 'draft';

  const load = useCallback(() => {
    if (!period) return;
    api.get(`/shifts/roster-periods/${period.id}/versions`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load the versions.'));
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setCompare(null);
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-2)));
  };
  const labelOf = (ref) => {
    if (ref === 'current') return 'Now';
    const i = data?.versions.findIndex((v) => v.id === ref);
    return i >= 0 ? `#${data.versions.length - i}` : `#${ref}`;
  };

  const runCompare = async (a, b) => {
    setBusy(true);
    try {
      const r = await api.get(`/shifts/roster-periods/${period.id}/versions/compare?a=${a}&b=${b}`);
      setCompare(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to compare.');
    } finally {
      setBusy(false);
    }
  };

  const restore = async (v) => {
    setBusy(true);
    try {
      const r = await api.post(`/shifts/roster-periods/${period.id}/versions/${v.id}/restore`);
      const missing = r.data.missing_slots;
      toast.success(`Version restored. The previous state is saved as "Before restore".${missing ? ` ${missing} shift${missing === 1 ? '' : 's'} of that version no longer exist.` : ''}`);
      setConfirm(null);
      setCompare(null);
      setSelected([]);
      load();
      onRestored?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to restore the version.');
    } finally {
      setBusy(false);
    }
  };

  const ordered = [...selected].sort((x, y) => y - x).reverse(); // older first

  return (
    <Modal isOpen onClose={onClose} title="Draft versions" size="lg">
      {!data ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : (
        <>
          <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: 'var(--text-light)', lineHeight: 1.5 }}>
            Every generation is saved here (the last {data.max_versions}). Tick two to compare them, or one to compare
            it with the plan as it is now.{isDraft ? ' Restoring saves the current state first, so it can be undone.' : ''}
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <button type="button" className="btn btn-primary btn-sm" style={{ minHeight: '36px' }}
              disabled={busy || selected.length !== 2} onClick={() => runCompare(ordered[0], ordered[1])}>
              Compare selected
            </button>
            <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '36px' }}
              disabled={busy || selected.length !== 1} onClick={() => runCompare(selected[0], 'current')}>
              Compare with now
            </button>
          </div>
          {confirm && (
            <div style={{ padding: '10px 12px', borderRadius: 'var(--radius)', background: 'var(--warning-light)', border: '1px solid var(--warning)', marginBottom: '12px', fontSize: '0.85rem' }}>
              <p style={{ margin: '0 0 8px' }}>
                Restore the version from <strong>{when(confirm.created_at)}</strong>? All assignments and pins of this
                draft are replaced. The current state is saved as &quot;Before restore&quot; first.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary btn-sm" style={{ minHeight: '36px' }} disabled={busy} onClick={() => restore(confirm)}>
                  {busy ? 'Restoring…' : 'Restore'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '36px' }} disabled={busy} onClick={() => setConfirm(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {data.versions.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No versions yet — they are saved from the next generation on.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {data.versions.map((v) => (
                <VersionRow
                  key={v.id}
                  v={v}
                  number={data.versions.length - data.versions.indexOf(v)}
                  current={v.id === data.current_version_id}
                  published={v.id === data.published_version_id}
                  selected={selected.includes(v.id)}
                  onSelect={toggle}
                  onRestore={setConfirm}
                  canRestore={isDraft}
                  ruleLabels={ruleLabels}
                />
              ))}
            </div>
          )}
          {compare && <Compare data={compare} labelOf={labelOf} onHighlight={(ids) => { onHighlight?.(ids); onClose(); }} />}
        </>
      )}
    </Modal>
  );
}
